/* Created by Interactive 63 - https://interactive63.com */
/**
 * The start screen: shown over the stage while no SVG is loaded. Offers a drop zone and file
 * picker, project file opening, three bundled samples, the import options and the list of recent
 * projects with rename, duplicate, download and delete.
 */
import { Component } from '../ui/component';
import { h } from '../ui/dom';
import type { ImportController } from '../import-controller';
import { studioLink } from '../panels/help-menu';
import { showContextMenu } from '../panels/timeline/context-menu';
import type { IndexEntry } from '../persistence/project-storage';
import { formatRelativeTime } from '../persistence/relative-time';
import type { ProjectSession } from '../persistence/session';

const SAMPLES = [
  { id: 'logo', label: 'Logo mark' },
  { id: 'icons', label: 'Line icons' },
  { id: 'scene', label: 'Landscape' },
];

export interface StartScreenProps {
  controller: ImportController;
  session: ProjectSession;
}

export class StartScreen extends Component<StartScreenProps> {
  private recent: HTMLElement | undefined;

  /** Creates the overlay container. */
  protected override createRoot(): HTMLElement {
    return h('div', { class: 'start-screen' });
  }

  /** Builds the content and hides the screen as soon as a project has elements. */
  protected override init(): void {
    const { controller, session } = this.props;
    const fileInput = h('input', {
      type: 'file',
      accept: '.svg,image/svg+xml',
      class: 'visually-hidden',
      'aria-label': 'Choose SVG file',
    });
    fileInput.addEventListener('change', () => {
      const file = fileInput.files?.[0];
      fileInput.value = '';
      if (file) void controller.importFile(file);
    });

    const clean = h('input', { type: 'checkbox', checked: true, id: 'import-clean' });
    clean.addEventListener('change', () => {
      controller.options.clean = clean.checked;
    });

    const simplify = h('input', { type: 'checkbox', checked: true, id: 'import-simplify' });
    simplify.addEventListener('change', () => {
      controller.options.simplify = simplify.checked;
    });

    const samples = SAMPLES.map((sample) =>
      h(
        'button',
        { type: 'button', class: 'sample', onClick: () => void controller.importSample(sample.id) },
        h('img', {
          src: `${import.meta.env.BASE_URL}samples/${sample.id}.svg`,
          alt: '',
          width: 120,
          height: 72,
        }),
        h('span', null, sample.label),
      ),
    );

    this.el.append(
      h(
        'div',
        { class: 'start-card' },
        h('h3', null, 'Animate an SVG'),
        h(
          'p',
          null,
          'Drop an SVG here, choose a file, or paste SVG code. Everything stays in your browser.',
        ),
        h(
          'div',
          { class: 'drop-zone' },
          h(
            'button',
            {
              type: 'button',
              class: 'primary',
              onClick: () => {
                fileInput.click();
              },
            },
            'Choose SVG file',
          ),
          h(
            'button',
            {
              type: 'button',
              onClick: () => {
                session.chooseProjectFile();
              },
            },
            'Open project file…',
          ),
          fileInput,
        ),
        h('h4', null, 'Or try a sample'),
        h('div', { class: 'samples' }, ...samples),
        h(
          'details',
          { class: 'import-options' },
          h('summary', null, 'Import options'),
          h('label', null, clean, ' Clean up SVG on import (recommended)'),
          h('label', null, simplify, ' Split into separate parts (recommended)'),
        ),
        h(
          'section',
          { class: 'recent', 'aria-label': 'Recent projects' },
          h('h4', null, 'Recent projects'),
          h('div', { class: 'recent-grid' }),
        ),
        h('p', { class: 'start-credit' }, 'Made by ', studioLink()),
      ),
    );
    this.recent = this.el.querySelector<HTMLElement>('.recent-grid') ?? undefined;

    this.select(
      (s) => Object.keys(s.project.elements).length === 0,
      (empty) => {
        this.el.hidden = !empty;
        if (empty) void this.refresh();
      },
      { immediate: true },
    );
  }

  /** Reloads the recent projects list from storage. */
  async refresh(): Promise<void> {
    const grid = this.recent;
    if (!grid) return;
    const entries = await this.props.session.recent();
    grid.replaceChildren(...entries.map((entry) => this.card(entry)));
    grid.parentElement?.toggleAttribute('hidden', entries.length === 0);
  }

  /** Builds one recent-project card. */
  private card(entry: IndexEntry): HTMLElement {
    const { session } = this.props;
    const picture = entry.thumbnail
      ? h('img', { src: entry.thumbnail, alt: '', width: 120, height: 72 })
      : h('div', { class: 'recent-placeholder', 'aria-hidden': 'true' }, '▱');
    const name = h('span', { class: 'recent-name' }, entry.name);
    const open = h(
      'button',
      {
        type: 'button',
        class: 'recent-open',
        title: `Open ${entry.name}`,
        onClick: () => void session.openStored(entry.id),
      },
      picture,
      name,
      h('span', { class: 'recent-time' }, formatRelativeTime(entry.updatedAt)),
    );
    const menu = h(
      'button',
      { type: 'button', class: 'recent-menu', 'aria-label': `Options for ${entry.name}` },
      '⋯',
    );
    menu.addEventListener('click', () => {
      const box = menu.getBoundingClientRect();
      showContextMenu(box.left, box.bottom, [
        {
          label: 'Rename',
          onSelect: () => {
            this.rename(name, entry);
          },
        },
        {
          label: 'Duplicate',
          onSelect: () => {
            void this.run(session.duplicate(entry.id));
          },
        },
        {
          label: 'Download project file',
          onSelect: () => {
            void session.download(entry.id);
          },
        },
        {
          label: 'Delete',
          onSelect: () => {
            if (window.confirm(`Delete '${entry.name}'? This can't be undone.`)) {
              void this.run(session.remove(entry.id));
            }
          },
        },
      ]);
    });
    return h('div', { class: 'recent-card' }, open, menu);
  }

  /** Waits for a storage change to finish, then redraws the list. */
  private async run(change: Promise<void>): Promise<void> {
    try {
      await change;
    } catch (error) {
      console.warn('Recent project change failed', error);
    }
    await this.refresh();
  }

  /** Swaps a card's name for a text field; Enter saves and Escape cancels. */
  private rename(name: HTMLElement, entry: IndexEntry): void {
    const input = h('input', {
      class: 'recent-rename',
      value: entry.name,
      'aria-label': 'Project name',
    });
    name.replaceWith(input);
    input.focus();
    input.select();
    let done = false;
    /** Restores the label, saving the typed name when requested. */
    const finish = (save: boolean): void => {
      if (done) return;
      done = true;
      if (save) void this.run(this.props.session.rename(entry.id, input.value));
      else input.replaceWith(name);
    };
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') finish(true);
      else if (event.key === 'Escape') finish(false);
    });
    input.addEventListener('blur', () => {
      finish(true);
    });
  }
}
